import { fabric } from 'fabric';
import {
  SUPPLIED_VALUE_PLACEHOLDER,
  suppliedValueDisplayText,
} from 'helpers/suppliedValue';
import { PERSONALIZED_VALUE_BACKGROUND } from 'components/Canvas/common/constants';

export { SUPPLIED_VALUE_PLACEHOLDER };

/**
 * Supplied value textbox
 *
 * Renders a value (typically a promo code) that is allocated from the
 * company's pool of supplied values at stitch time. The value is not known
 * until the ad is fulfilled, so the canvas shows a stand-in and the var is
 * configured entirely from its metadata:
 *
 *   company_group_id - required - which series of values to pull from
 *   start / end      - optional - the allocation window, ISO8601
 *   ttl_days         - optional - days from issuance until the value expires
 *   display_value    - optional - a representative value to stand in for the
 *                                 real one, so sizing can be checked
 *   export_key       - optional - names a template param this var publishes its
 *                                 expiry date under, for a separate var to
 *                                 render
 *
 * This object renders the value alone. The expiry date is published under
 * export_key rather than appended, so it is a separate canvas object the
 * designer positions and words themselves.
 */
export class SuppliedValueObject extends fabric.Textbox {
  static type = 'SuppliedValue';
  label = '';

  initialize(text, options) {
    // the value comes from the supplied values pool at stitch time, so the
    // text on the canvas is a stand-in and must not be editable.
    options.editable = false;
    this.label = options.label;
    // the whole object is a personalized value, so it carries the same grey
    // the personalization tokens in dynamic text use. Editor affordance only,
    // it is never persisted.
    options.textBackgroundColor = PERSONALIZED_VALUE_BACKGROUND;
    super.initialize(
      text || suppliedValueDisplayText(options.metadata),
      options
    );

    this.on('mousewheel', function () {
      this.canvas.renderAll();
      this._renderControls(this.canvas.contextTop, {
        hasControls: false,
      });
    });

    this.on('mouseup', e => {
      // the whole object is the var, so clicking anywhere on it opens the
      // config menu the same way clicking a token in dynamic text does.
      // fabric's isClick distinguishes a real click from the mouseup that
      // ends a move or a resize.
      if (e.button === 1) {
        if (e.isClick) {
          this.triggerKeysMenu();
        }
      } else if (e.button === 3) {
        const canvasEl = document.getElementById('canvas');
        const canvasPosition = {
          left: canvasEl?.getBoundingClientRect().left,
          top: canvasEl?.getBoundingClientRect().top,
        };
        const eventData = {
          object: this,
          visible: true,
          position: {
            left: canvasPosition.left + e.pointer.x + 8,
            top: canvasPosition.top + e.pointer.y - 24,
          },
        };
        this.canvas.fire('object:rightclicked', eventData);
        this.canvas.setActiveObject(this);
      }
    });

    return this;
  }

  /**
   * Re-renders the stand-in text from the current metadata.
   *
   * The rendered text is derived from display_value rather than stored
   * independently, so this has to be called whenever that changes for the
   * canvas to catch up. ttl_days no longer affects it: the expiry belongs to
   * the var consuming this one's export_key.
   */
  refreshDisplayText() {
    this.set('text', suppliedValueDisplayText(this.metadata));
    // the box keeps its width, but the new text has to be re-wrapped and
    // re-measured against it before the canvas redraws.
    this.initDimensions();
    this.setCoords();
    this.canvas?.requestRenderAll();
  }

  /**
   * Opens the config menu for this var, positioned just below the object.
   * Called both by clicking the object and by the toolbox button.
   */
  triggerKeysMenu() {
    this.setCoords();
    const canvasPosition = this.getCanvasBoundingClientRect();
    const zoom = this.canvas.getZoom();
    const { scaleX, scaleY, width, height } = this;
    const boundingRect = this.getBoundingRect(false);
    const padLeft = (width * scaleX * zoom - width) / 2;
    const padTop = (height * scaleY * zoom - height) / 2;
    const eventData = {
      object: this,
      isEditing: true,
      visible: true,
      position: {
        left:
          canvasPosition.left +
          boundingRect.left +
          padLeft +
          boundingRect.width / 2,
        top:
          canvasPosition.top +
          boundingRect.top +
          padTop +
          boundingRect.height,
      },
    };
    this.canvas.fire('text:key:selected', eventData);
  }

  getCanvasBoundingClientRect() {
    const canvasEl = document.getElementById('canvas');
    const position = {
      left: canvasEl?.getBoundingClientRect().left,
      top: canvasEl?.getBoundingClientRect().top,
    };
    return position;
  }

  // allows width of box to be set regardless of text value, adds an ellipsis if text is longer than width
  _wrapLine(_line, lineIndex, desiredWidth, reservedSpace) {
    const offset = 0;
    let textWidth = this._measureWord(_line, lineIndex, offset);

    if (textWidth > desiredWidth && !this.isEditing) {
      const ellipsis = '…';
      const lengthReducingFactor = desiredWidth / textWidth;
      let length = Math.ceil(_line.length * lengthReducingFactor);
      while (textWidth >= desiredWidth && length > 0) {
        _line = _line.substring(0, length--) + ellipsis;
        textWidth = this._measureWord(_line, lineIndex, offset);
      }
    }

    return super._wrapLine(_line, lineIndex, desiredWidth, reservedSpace);
  }

  toObject(propertiesToInclude = []) {
    return fabric.util.object.extend(
      super.toObject.call(this, propertiesToInclude),
      {
        text: this.text,
        clipPath: this.clipPath,
        // only carry a label that was actually set, otherwise an empty one
        // overrides the res_type default in fabricToObject and the server
        // rejects the var for a blank label.
        ...(this.label && { label: this.label }),
      }
    );
  }

  toJSON(propertiesToInclude = []) {
    return fabric.util.object.extend(
      super.toObject.call(this, propertiesToInclude),
      {
        text: this.text,
        clipPath: this.clipPath,
        // only carry a label that was actually set, otherwise an empty one
        // overrides the res_type default in fabricToObject and the server
        // rejects the var for a blank label.
        ...(this.label && { label: this.label }),
      }
    );
  }

  static fromObject(options, callback) {
    const { text, ...textOptions } = options;
    return callback && callback(new fabric.SuppliedValue(text, textOptions));
  }
}

fabric.SuppliedValue = fabric.util.createClass(SuppliedValueObject, {
  type: SuppliedValueObject.type,
});
fabric.SuppliedValue.fromObject = SuppliedValueObject.fromObject;
